<ng-container *transloco="let t">
  <ld-base-modal [title]="modalTitle" [isLoading]="isLoading()" [closeOnBackdropClick]="false" (closeModal)="onClose()">
    <div modal-content>
      @if (isLoading()) {
        <div
          id="link-loan-loading-overlay"
          class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-card bg-white/90"
          role="status"
          aria-live="polite"
        >
          <svg class="h-12 w-12 animate-spin text-ld-primary" viewBox="0 0 24 24" fill="none">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p class="mt-4 font-dm text-base font-medium text-ld-neutral-700">{{ t('modalsLinkLoanVerifying') }}</p>
        </div>
      }

      <p
        id="link-loan-description"
        class="font-dm text-base font-medium text-ld-neutral-500 leading-6 tracking-[0.16px] mb-6"
      >
        {{ t('modalsLinkLoanHelocDescription') }}
      </p>

      @if (showCallSupport) {
        <div id="link-loan-call-support" class="mb-4 rounded-lg border border-ld-error/30 bg-red-50 p-4" role="alert">
          <div class="flex gap-3">
            <img [src]="errorIcon" alt="Error" class="w-6 h-6 shrink-0" />
            <div>
              <p id="link-loan-call-support-header" class="font-dm text-sm font-semibold text-ld-neutral">
                {{ t('modalsLinkLoanCallSupportHeader') }}
              </p>
              <p id="link-loan-call-support-message" class="font-dm text-sm text-ld-neutral-500">
                {{ t('modalsLinkLoanCallSupportMessage1') }}
                <a
                  id="link-loan-call-support-phone"
                  [href]="customerServiceTel"
                  class="font-semibold text-ld-link hover:text-ld-link-hover"
                >
                  {{ customerServicePhone }}
                </a>
                {{ t('modalsLinkLoanCallSupportMessage2') }}
              </p>
            </div>
          </div>
        </div>
      }

      <form id="link-loan-form" [formGroup]="linkLoanForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-5">
        <div id="link-loan-number-field" class="flex flex-col">
          <mat-form-field
            appearance="outline"
            class="w-full mat-form-field-pill link-loan-field"
            subscriptSizing="dynamic"
          >
            <mat-label>{{ t('commonFieldsLoanNumber') }}</mat-label>
            <input
              matInput
              ldNumericOnly
              id="link-loan-number-input"
              type="text"
              inputmode="numeric"
              formControlName="loanNumber"
              maxlength="10"
              autocomplete="off"
              aria-required="true"
              (blur)="onLoanNumberBlur()"
            />
            <mat-error id="link-loan-number-error">{{ getFieldError('loanNumber') }}</mat-error>
          </mat-form-field>
          @if (errorMessage() && !showCallSupport) {
            <p id="link-loan-number-api-error" class="px-4 font-dm text-sm text-ld-error leading-5" role="alert">
              {{ t('modalsLinkLoanHelocErrorsLoanNumber') }}
            </p>
          }
          <p id="link-loan-number-hint" class="px-4 font-dm text-sm text-ld-neutral-500 leading-5">
            {{ t('modalsLinkLoanLoanNumberHint') }}
          </p>
        </div>

        <div id="link-loan-zip-field" class="flex flex-col">
          <mat-form-field
            appearance="outline"
            class="w-full mat-form-field-pill link-loan-field"
            subscriptSizing="dynamic"
          >
            <mat-label>{{ t('modalsLinkLoanPropertyZip') }}</mat-label>
            <input
              matInput
              ldNumericOnly
              id="link-loan-zip-input"
              type="text"
              inputmode="numeric"
              formControlName="zipCode"
              maxlength="5"
              autocomplete="postal-code"
              aria-required="true"
            />
            <mat-error id="link-loan-zip-error">{{ getFieldError('zipCode') }}</mat-error>
          </mat-form-field>
          <p id="link-loan-zip-hint" class="px-4 font-dm text-sm text-ld-neutral-500 leading-5">
            {{ t('modalsLinkLoanPropertyZipHint') }}
          </p>
        </div>

        <div id="link-loan-ssn-field" class="flex flex-col">
          <mat-form-field
            appearance="outline"
            class="w-full mat-form-field-pill link-loan-field"
            subscriptSizing="dynamic"
          >
            <mat-label>{{ t('ssnLast4') }}</mat-label>
            <input
              matInput
              ldNumericOnly
              id="link-loan-ssn-input"
              [type]="hideSSN() ? 'password' : 'text'"
              inputmode="numeric"
              formControlName="ssn"
              maxlength="4"
              autocomplete="off"
              aria-required="true"
              glance_masked="true"
            />
            <button
              matSuffix
              type="button"
              mat-icon-button
              id="link-loan-ssn-toggle"
              (click)="toggleSSNVisibility(); $event.stopPropagation()"
              [attr.aria-label]="hideSSN() ? t('commonLabelsShowSSN') : t('commonLabelsHideSSN')"
              class="cursor-pointer"
            >
              <mat-icon>{{ hideSSN() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error id="link-loan-ssn-error">{{ getFieldError('ssn') }}</mat-error>
          </mat-form-field>
        </div>

        <button id="link-loan-hidden-submit" type="submit" class="hidden" aria-hidden="true" tabindex="-1"></button>
      </form>
    </div>

    <div modal-footer>
      <button
        id="link-loan-submit-btn"
        type="submit"
        class="btn-primary-ld-purple"
        [disabled]="isLoading() || !isFormValid"
        (click)="onSubmit()"
      >
        @if (isLoading()) {
          <span class="flex items-center justify-center gap-2">
            <svg class="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>{{ t('modalsLinkLoanVerifying') }}</span>
          </span>
        } @else {
          {{ submitButtonText }}
        }
      </button>
    </div>
  </ld-base-modal>
</ng-container>
